import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { BACKEND_URL } from '../utils/config';
import {
  alignLeft,
  alignCenter,
  alignRight,
  alignTop,
  alignMiddle,
  alignBottom,
  distributeHorizontally,
  distributeVertically
} from '../utils/positioningUtils';
import { selectHasPersonImage, selectPersonImageDisplayProps } from '../redux/selectors/personImageSelectors';
import usePersonImages from './usePersonImages';

const useMyClosetActions = ({
  // State
  droppedItems,
  setDroppedItems,
  selectedItem,
  setSelectedItem,
  multiSelectedItems,
  setMultiSelectedItems,
  lookName,
  setLookName,
  setIsLookPopupOpen,
  setIsNoItemsPopupOpen,
  setIsSuccessPopupOpen,
  background,
  setBackground,
  virtualTryOnResults,
  setVirtualTryOnResults,
  clothingItems,
  setClothingItems,
  selectedItems,
  setSelectedItems,
  setIsBulkSelectMode,

  // Virtual try-on
  isVirtualMode,
  personImage, // This is now overridden with Redux state in MyCloset
  toggleMode,
  updatePersonImage,
  clearTryOnError,


  // Props
  guest,
  showErrorModal,
  showVTOLoginModal,
  navigate
}) => {

  // Get Redux person image state for VTO dropdown behavior
  const hasPersonImageRedux = useSelector(selectHasPersonImage);
  const personImagePropsRedux = useSelector(selectPersonImageDisplayProps);

  // Get actual person image data from backend integration
  const { activePersonImage } = usePersonImages();
  const personImageRedux = activePersonImage?.image?.url || null;

  // Look management
  const handleCreateLook = useCallback(() => {
    if (droppedItems.length === 0) {
      setIsNoItemsPopupOpen(true);
      setTimeout(() => setIsNoItemsPopupOpen(false), 3000);
    } else {
      setIsLookPopupOpen(true);
    }
  }, [droppedItems.length, setIsLookPopupOpen, setIsNoItemsPopupOpen]);

  const saveLook = useCallback(async () => {
    const look = droppedItems.map(item => ({
      id: item.id,
      image: item.image,
      x: item.x,
      y: item.y,
      width: item.width,
      height: item.height,
      rotation: item.rotation || 0,
      zIndex: item.zIndex || 1,
      isVirtualTryOn: item.isVirtualTryOn || false,
      originalImage: item.originalImage,
      tryOnMetadata: item.tryOnMetadata
    }));

    // Save to localStorage
    try {
      const existingLooks = JSON.parse(localStorage.getItem('savedLooks') || '[]');
      const newLook = {
        id: Date.now(),
        name: lookName,
        items: look,
        background: background,
        hasVirtualTryOn: droppedItems.some(item => item.isVirtualTryOn),
        virtualTryOnResults: virtualTryOnResults
      };
      existingLooks.push(newLook);
      localStorage.setItem('savedLooks', JSON.stringify(existingLooks));
    } catch (error) {
      console.error('Error saving look to localStorage:', error);
    }

    // If user is logged in, save to backend
    if (!guest) {
      try {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');

        await axios(`${BACKEND_URL}/closet-looks`, {
          method: 'post',
          data: {
            lookName: lookName,
            lookData: look,
            backgroundData: background,
            closet_clothes: droppedItems.map(item => item.id),
            users_permissions_user: user,
            hasVirtualTryOn: droppedItems.some(item => item.isVirtualTryOn),
            virtualTryOnResults: virtualTryOnResults
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } catch (error) {
        console.error('Error saving look to backend:', error);
      }
    }

    setIsLookPopupOpen(false);
    setLookName('');
    setIsSuccessPopupOpen(true);
    setTimeout(() => setIsSuccessPopupOpen(false), 3000);
  }, [droppedItems, lookName, background, virtualTryOnResults, guest, setIsLookPopupOpen, setLookName, setIsSuccessPopupOpen]);

  // Item deletion
  const getSelectedDroppedItems = useCallback(() => {
    if (multiSelectedItems.length > 0) {
      return droppedItems.filter(item => multiSelectedItems.includes(item.id));
    } else if (selectedItem !== null) {
      return [droppedItems[selectedItem]];
    }
    return [];
  }, [multiSelectedItems, selectedItem, droppedItems]);

  const saveCurrentLook = useCallback(() => {
    if (droppedItems.length > 0) {
      const look = droppedItems.map(item => ({
        id: item.id,
        image: item.image,
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
        rotation: item.rotation || 0,
        zIndex: item.zIndex || 1,
        isVirtualTryOn: item.isVirtualTryOn || false,
        originalImage: item.originalImage,
        tryOnMetadata: item.tryOnMetadata
      }));
      localStorage.setItem('currentLook', JSON.stringify(look));

      if (virtualTryOnResults.length > 0) {
        localStorage.setItem('currentVirtualTryOnResults', JSON.stringify(virtualTryOnResults));
      }
    } else {
      localStorage.removeItem('currentLook');
      localStorage.removeItem('currentVirtualTryOnResults');
    }
  }, [droppedItems, virtualTryOnResults]);

  const handleDeleteSelection = useCallback(() => {
    if (selectedItem !== null) {
      const newItems = droppedItems.filter((_, index) => index !== selectedItem);
      setDroppedItems(newItems, 'delete-single');
      setSelectedItem(null);
    } else if (multiSelectedItems.length > 0) {
      // Handle multi-selected items
      const newItems = droppedItems.filter(item => !multiSelectedItems.includes(item.id));
      setDroppedItems(newItems, 'delete');
      setMultiSelectedItems([]);
    }
  }, [selectedItem, multiSelectedItems, droppedItems, setDroppedItems, setSelectedItem, setMultiSelectedItems]);

  const handleDeleteAll = useCallback(() => {
    setDroppedItems([], 'delete-all');
    setSelectedItem(null);
    localStorage.removeItem('currentLook');
  }, [setDroppedItems, setSelectedItem]);

  const handleDeleteSelected = useCallback(() => {
    const selectedItemsData = getSelectedDroppedItems();
    if (selectedItemsData.length > 0) {
      const selectedIds = selectedItemsData.map(item => item.id);
      const newItems = droppedItems.filter(item => !selectedIds.includes(item.id));
      setDroppedItems(newItems, 'delete');
      setMultiSelectedItems([]);
      setSelectedItem(null);
    }
  }, [getSelectedDroppedItems, droppedItems, setDroppedItems, setMultiSelectedItems, setSelectedItem]);

  // Navigation
  const handleHomeClick = useCallback(() => {
    saveCurrentLook();
    navigate(guest ? '/' : '/');
  }, [saveCurrentLook, guest, navigate]);

  const handleFolderClick = useCallback(() => {
    saveCurrentLook();
    navigate(guest ? '/guest-lookbook' : '/lookbook');
  }, [saveCurrentLook, guest, navigate]);

  // Background management
  const handleBackgroundChange = useCallback((newBackground) => {
    setBackground(newBackground);
  }, [setBackground]);

  const handleBackgroundImageUpload = useCallback((file, showErrorModal) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      if (showErrorModal) {
        showErrorModal({
          title: 'Invalid File Type',
          message: 'Please select a valid image file (JPG, PNG, or GIF)',
          type: 'warning',
          icon: 'file image'
        });
      }
      return;
    }

    const maxSize = 20 * 1024 * 1024; // Increased to 20MB
    if (file.size > maxSize) {
      if (showErrorModal) {
        showErrorModal({
          title: 'File Too Large',
          message: 'Image file is too large. Please select an image smaller than 20MB.',
          type: 'warning',
          icon: 'warning sign'
        });
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const maxWidth = 1920;
        const maxHeight = 1080;

        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);
        const optimizedImageUrl = canvas.toDataURL('image/jpeg', 0.8);

        setBackground({
          type: 'image',
          imageUrl: optimizedImageUrl,
          originalName: file.name
        });
      };
      img.src = imageUrl;
    };

    reader.onerror = () => {
      if (showErrorModal) {
        showErrorModal({
          title: 'File Read Error',
          message: 'Error reading the image file. Please try again.',
          type: 'error',
          icon: 'exclamation triangle'
        });
      }
    };

    reader.readAsDataURL(file);
  }, [setBackground]);

  // Mode management
  const handleModeChange = useCallback((mode) => {
    console.log('[Closet] VTO Mode Change Debug:', {
      mode,
      isVirtualMode,
      hasPersonImageRedux,
      personImageRedux: !!personImageRedux,
      personImageLocal: !!personImage,
      personImageReduxType: typeof personImageRedux,
      personImageLocalType: typeof personImage
    });

    if (mode === 'virtual' && !isVirtualMode) {
      // Check authentication before switching to VTO mode
      const user = localStorage.getItem('user');
      const token = localStorage.getItem('token');

      if (!user || !token) {
        console.log('[Closet] User not authenticated, showing VTO login modal');
        // Show VTO Login Confirmation Portal
        if (showVTOLoginModal) {
          showVTOLoginModal();
        }
        return;
      }

      // Clear all selections when switching to VTO mode
      setSelectedItem(null);
      setMultiSelectedItems([]);

      // Switch to virtual mode directly - canvas will show upload interface if no person image
      console.log('[Closet] Switching to virtual mode - canvas will handle upload interface');
      toggleMode(true);
    } else if (mode === 'manual' && isVirtualMode) {
      console.log('[Closet] Switching to manual mode');
      toggleMode(false);
    }

    clearTryOnError();
  }, [isVirtualMode, toggleMode, clearTryOnError, setSelectedItem, setMultiSelectedItems]);

  const handlePersonImageUpload = useCallback(async (imageDataUrl, qualityMessage, showErrorModal) => {
    try {
      // NO API CALLS during upload - just store the image without availability check
      // We'll skip updatePersonImage to avoid the API call and handle this in MyCloset component

      if (qualityMessage) {
        console.log('Image quality feedback:', qualityMessage);
      }
    } catch (error) {
      console.error('Error uploading person image:', error);
      if (showErrorModal) {
        showErrorModal({
          title: 'Upload Failed',
          message: 'Failed to upload person image. Please try again.',
          type: 'error',
          icon: 'exclamation triangle'
        });
      }
    }
  }, []);

  // Guest mode handlers
  const handleGuestUpload = useCallback((newItem) => {
    const guestClothes = JSON.parse(localStorage.getItem('guestClothes') || '[]');
    const itemWithId = { ...newItem, id: Date.now() };
    guestClothes.push(itemWithId);
    localStorage.setItem('guestClothes', JSON.stringify(guestClothes));
    setClothingItems(guestClothes);
  }, [setClothingItems]);

  const handleGuestDelete = useCallback((itemId) => {
    const guestClothes = JSON.parse(localStorage.getItem('guestClothes') || '[]');
    const updatedClothes = guestClothes.filter(item => item.id !== itemId);
    localStorage.setItem('guestClothes', JSON.stringify(updatedClothes));
    setClothingItems(updatedClothes);
  }, [setClothingItems]);

  // Bulk operations
  const handleBulkDelete = useCallback(() => {
    if (window.confirm('Are you sure you want to delete selected items?')) {
      if (guest) {
        try {
          const guestClothes = JSON.parse(localStorage.getItem('guestClothes') || '[]');
          const updatedClothes = guestClothes.filter(item => !selectedItems.includes(item.id));
          localStorage.setItem('guestClothes', JSON.stringify(updatedClothes));
          setClothingItems(updatedClothes);
        } catch (error) {
          console.error('Error deleting guest items:', error);
        }
      } else {
        const token = localStorage.getItem('token');
        Promise.all(
          selectedItems.map(id =>
            axios.delete(`${BACKEND_URL}/closet-clothes/${id}`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          )
        ).then(() => {
          setClothingItems(prevItems =>
            prevItems.filter(item => !selectedItems.includes(item.id))
          );
        }).catch(error => {
          console.error('Error deleting items:', error);
        });
      }
      setSelectedItems([]);
      setIsBulkSelectMode(false);
    }
  }, [guest, selectedItems, setClothingItems, setSelectedItems, setIsBulkSelectMode]);

  // Positioning helpers

  const updateSelectedItems = useCallback((updatedItems, actionType = 'align') => {
    const updatedItemsMap = new Map(updatedItems.map(item => [item.id, item]));
    const newItems = droppedItems.map(item => updatedItemsMap.get(item.id) || item);
    setDroppedItems(newItems, actionType);
  }, [droppedItems, setDroppedItems]);

  // Alignment handlers
  const handleAlignLeft = useCallback(() => {
    const selectedItemsData = getSelectedDroppedItems();
    if (selectedItemsData.length > 0) {
      const alignedItems = alignLeft(selectedItemsData);
      updateSelectedItems(alignedItems, 'align-left');
    }
  }, [getSelectedDroppedItems, updateSelectedItems]);

  const handleAlignCenter = useCallback(() => {
    const selectedItemsData = getSelectedDroppedItems();
    if (selectedItemsData.length > 0) {
      const alignedItems = alignCenter(selectedItemsData);
      updateSelectedItems(alignedItems, 'align-center');
    }
  }, [getSelectedDroppedItems, updateSelectedItems]);

  const handleAlignRight = useCallback(() => {
    const selectedItemsData = getSelectedDroppedItems();
    if (selectedItemsData.length > 0) {
      const alignedItems = alignRight(selectedItemsData);
      updateSelectedItems(alignedItems, 'align-right');
    }
  }, [getSelectedDroppedItems, updateSelectedItems]);

  const handleAlignTop = useCallback(() => {
    const selectedItemsData = getSelectedDroppedItems();
    if (selectedItemsData.length > 0) {
      const alignedItems = alignTop(selectedItemsData);
      updateSelectedItems(alignedItems, 'align-top');
    }
  }, [getSelectedDroppedItems, updateSelectedItems]);

  const handleAlignMiddle = useCallback(() => {
    const selectedItemsData = getSelectedDroppedItems();
    if (selectedItemsData.length > 0) {
      const alignedItems = alignMiddle(selectedItemsData);
      updateSelectedItems(alignedItems, 'align-middle');
    }
  }, [getSelectedDroppedItems, updateSelectedItems]);

  const handleAlignBottom = useCallback(() => {
    const selectedItemsData = getSelectedDroppedItems();
    if (selectedItemsData.length > 0) {
      const alignedItems = alignBottom(selectedItemsData);
      updateSelectedItems(alignedItems, 'align-bottom');
    }
  }, [getSelectedDroppedItems, updateSelectedItems]);

  const handleDistributeHorizontally = useCallback(() => {
    const selectedItemsData = getSelectedDroppedItems();
    if (selectedItemsData.length >= 3) {
      const distributedItems = distributeHorizontally(selectedItemsData);
      updateSelectedItems(distributedItems, 'distribute-horizontal');
    }
  }, [getSelectedDroppedItems, updateSelectedItems]);

  const handleDistributeVertically = useCallback(() => {
    const selectedItemsData = getSelectedDroppedItems();
    if (selectedItemsData.length >= 3) {
      const distributedItems = distributeVertically(selectedItemsData);
      updateSelectedItems(distributedItems, 'distribute-vertical');
    }
  }, [getSelectedDroppedItems, updateSelectedItems]);

  // Multi-selection handlers
  const handleSelectAll = useCallback(() => {
    if (droppedItems.length > 0) {
      setMultiSelectedItems(droppedItems.map(item => item.id));
      setSelectedItem(null);
    }
  }, [droppedItems, setMultiSelectedItems, setSelectedItem]);

  const handleClearSelection = useCallback(() => {
    setMultiSelectedItems([]);
    setSelectedItem(null);
  }, [setMultiSelectedItems, setSelectedItem]);

  const handleNudge = useCallback((deltaX, deltaY, multiplier = 1) => {
    const selectedItemsData = getSelectedDroppedItems();
    if (selectedItemsData.length > 0) {
      const nudgedItems = selectedItemsData.map(item => ({
        ...item,
        x: item.x + (deltaX * multiplier),
        y: item.y + (deltaY * multiplier)
      }));
      updateSelectedItems(nudgedItems, 'nudge');
    }
  }, [getSelectedDroppedItems, updateSelectedItems]);

  // Utility functions

  return {
    // Look management
    handleCreateLook,
    saveLook,

    // Item deletion
    handleDeleteSelection,
    handleDeleteAll,
    handleDeleteSelected,

    // Navigation
    handleHomeClick,
    handleFolderClick,

    // Background management
    handleBackgroundChange,
    handleBackgroundImageUpload,

    // Mode management
    handleModeChange,
    handlePersonImageUpload,

    // Guest mode
    handleGuestUpload,
    handleGuestDelete,

    // Bulk operations
    handleBulkDelete,

    // Positioning
    handleAlignLeft,
    handleAlignCenter,
    handleAlignRight,
    handleAlignTop,
    handleAlignMiddle,
    handleAlignBottom,
    handleDistributeHorizontally,
    handleDistributeVertically,

    // Multi-selection
    handleSelectAll,
    handleClearSelection,
    handleNudge,

    // Utilities
    saveCurrentLook,
    getSelectedDroppedItems
  };
};

export default useMyClosetActions;